import type { TaskScopedList, TaskStatus } from "@mind/contracts/browser";
import type { WorkSearch } from "./searchState.js";

type BoardTask = TaskScopedList["items"][number];
export type WorkColumn = { readonly status: TaskStatus; readonly items: readonly BoardTask[] };

export const WORK_STATUSES: readonly TaskStatus[] = ["inbox", "planned", "in_progress", "review", "done"];

export function compareRanks(left: string, right: string): number {
  const leftRank = BigInt(left);
  const rightRank = BigInt(right);
  return leftRank < rightRank ? -1 : leftRank > rightRank ? 1 : 0;
}

export function deriveWorkColumns(
  items: readonly BoardTask[],
  search: Pick<WorkSearch, "project" | "status" | "filter">,
): readonly WorkColumn[] {
  const normalizedFilter = search.filter?.trim().toLocaleLowerCase() ?? "";
  const visible = items.filter((task) => {
    if (search.project !== undefined && task.projectId !== search.project) return false;
    if (search.status !== undefined && task.status !== search.status) return false;
    if (normalizedFilter.length > 0) {
      const searchable = [task.title, ...task.labels].join(" ").toLocaleLowerCase();
      if (!searchable.includes(normalizedFilter)) return false;
    }
    return true;
  });

  return WORK_STATUSES.map((status) => ({
    status,
    items: visible
      .filter((task) => task.status === status)
      .slice()
      .sort((left, right) => compareRanks(left.rank, right.rank) || left.id.localeCompare(right.id)),
  }));
}

export function statusLabelKey(status: TaskStatus): string {
  return STATUS_LABEL_KEYS[status];
}

const STATUS_LABEL_KEYS: Record<TaskStatus, string> = {
  inbox: "work.statuses.inbox",
  planned: "work.statuses.planned",
  in_progress: "work.statuses.inProgress",
  review: "work.statuses.review",
  done: "work.statuses.done",
};

export type MoveNeighbors = {
  readonly beforeTaskId: string | null;
  readonly afterTaskId: string | null;
};

export function moveNeighborsForDrop(
  items: readonly BoardTask[],
  taskId: string,
  toStatus: TaskStatus,
  insertionIndex?: number,
): MoveNeighbors {
  const destination = items
    .filter((task) => task.status === toStatus && task.id !== taskId)
    .slice()
    .sort((left, right) => compareRanks(left.rank, right.rank) || left.id.localeCompare(right.id));
  const boundedIndex = insertionIndex === undefined
    ? destination.length
    : Math.max(0, Math.min(insertionIndex, destination.length));
  return {
    beforeTaskId: destination[boundedIndex - 1]?.id ?? null,
    afterTaskId: destination[boundedIndex]?.id ?? null,
  };
}

export function projectScopedMoveNeighbors(
  items: readonly BoardTask[],
  projectId: string,
  taskId: string,
  toStatus: TaskStatus,
  insertionIndex?: number,
): MoveNeighbors {
  return moveNeighborsForDrop(items.filter((task) => task.projectId === projectId), taskId, toStatus, insertionIndex);
}

export function appendMoveNeighbors(items: readonly BoardTask[], taskId: string): MoveNeighbors {
  const moving = items.find((task) => task.id === taskId);
  return moveNeighborsForDrop(items, taskId, moving?.status ?? "inbox");
}
